<script setup lang="ts">
  import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
  import { useForm } from 'vee-validate'
  import { debounce, throttle } from '@/lib/timing'
  import { useSavingIndicatorStore } from '@/store/savingIndicatorStore'

  const autoSavingIndicatorStore = useSavingIndicatorStore()

  const props = defineProps({
    debounceDelay: {
      type: Number,
      default: 1000
    },
    throttleInterval: {
      type: Number,
      default: 1000
    },
    preventInitialSave: {
      type: Boolean,
      default: false
    },
    initialDelay: {
      type: Number,
      default: 500
    }
  })

  const { values, meta, validate } = useForm()
  const emit = defineEmits(['save'])

  const touchedFields = ref<Record<string, boolean>>({})
  const initialValuesSnapshot = ref<any>(null)
  const isInitialized = ref(false)

  // Set initialized to true after component is mounted with a delay
  onMounted(() => {
    if (props.preventInitialSave) {
      setTimeout(() => {
        isInitialized.value = true
      }, props.initialDelay)
    } else {
      isInitialized.value = true
    }
  })

  const createSnapshot = (obj: any): any => {
    if (obj === null || typeof obj !== 'object') {
      return obj
    }
    if (Array.isArray(obj)) {
      return obj.map(createSnapshot)
    }
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, createSnapshot(value)])
    )
  }

  const debouncedSave = debounce((values: any) => {
    autoSavingIndicatorStore.indicateSaving()
    emit('save', values)
  }, props.debounceDelay)

  // Flush any pending saves before the component unmounts
  onBeforeUnmount(() => {
    debouncedSave.flush()
  })

  const throttledSave = throttle((values: any) => {
    debouncedSave(values)
  }, props.throttleInterval)

  watch(
    values,
    async (newValues, oldValues) => {
      // Take a snapshot of initial values to prevent initial save
      if (initialValuesSnapshot.value === null) {
        initialValuesSnapshot.value = createSnapshot(newValues)
        return
      }

      // Skip save if not initialized yet or preventInitialSave is true and we haven't had any user interactions
      if (
        !isInitialized.value ||
        (props.preventInitialSave && Object.keys(touchedFields.value).length === 0)
      ) {
        return
      }

      const resp = await validate()
      if (resp.valid) {
        await throttledSave(newValues)
      }
    },
    { deep: true }
  )

  const handleFieldTouch = (fieldName: string) => {
    touchedFields.value[fieldName] = true
  }

  defineExpose({
    handleFieldTouch
  })
</script>

<template>
  <div>
    <slot />
  </div>
</template>
